import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { MODAL_SLIDE_PS_RESYNC_MS } from "@constants/modalAnimation";
import {
  clampPerfectScrollbarOverflow,
  getPerfectScrollbarMaxTop,
} from "@utils/perfectScrollbarScrollMetrics";

const STICK_TO_BOTTOM_THRESHOLD_PX = 32;
const CONTENT_CHANGE_SNAP_BURST_MS = 600;

/**
 * Modal 內 PerfectScrollbar：開場動畫／非同步內容載入後尺寸才穩定，
 * 需在 mount、動畫結束與內容變更時重新 update()，捲軸才會在溢出當下出現。
 *
 * stickToBottomOnContentChange（預設 true）：
 * 使用者在底部時，內容展開／收合變高變矮都會立刻對齊新底部（與帳單明細相同策略）。
 * 請將回傳的 handleScrollY 接到 PerfectScrollbar 的 onScrollY。
 */
export function useModalPerfectScrollbarResync(
  syncDeps = [],
  { enabled = true, stickToBottomOnContentChange = true } = {}
) {
  const scrollRef = useRef(null);
  const psRef = useRef(null);
  const contentRef = useRef(null);
  const stickToBottomRef = useRef(false);
  const scrollSnapBurstRafRef = useRef(0);

  const getScrollContainer = useCallback(
    () => scrollRef.current?._container ?? null,
    []
  );

  const getContentMaxTop = useCallback((container) => {
    if (!container) return 0;
    return getPerfectScrollbarMaxTop(container, contentRef.current);
  }, []);

  const updateStickToBottomFromScroll = useCallback(
    (container) => {
      if (!container) return;
      const maxTop = getContentMaxTop(container);
      stickToBottomRef.current =
        container.scrollTop >= Math.max(0, maxTop) - STICK_TO_BOTTOM_THRESHOLD_PX;
    },
    [getContentMaxTop]
  );

  const snapScrollPosition = useCallback(() => {
    const container = getScrollContainer();
    if (!container) return;
    const maxTop = getContentMaxTop(container);
    container.style.scrollBehavior = "auto";
    const overflow = container.scrollTop > maxTop + 0.5;
    const stick =
      stickToBottomOnContentChange && stickToBottomRef.current;
    if (stick || overflow) {
      container.scrollTop = maxTop;
    }
    updateStickToBottomFromScroll(container);
  }, [
    getContentMaxTop,
    getScrollContainer,
    stickToBottomOnContentChange,
    updateStickToBottomFromScroll,
  ]);

  const runUpdate = useCallback(() => {
    if (stickToBottomOnContentChange) {
      snapScrollPosition();
    } else {
      const container = getScrollContainer();
      if (container) {
        clampPerfectScrollbarOverflow(container, contentRef.current);
      }
    }
    psRef.current?.update?.();
    scrollRef.current?.updateScroll?.();
  }, [getScrollContainer, snapScrollPosition, stickToBottomOnContentChange]);

  const scheduleUpdate = useCallback(() => {
    requestAnimationFrame(() => {
      runUpdate();
      requestAnimationFrame(runUpdate);
    });
    window.setTimeout(runUpdate, MODAL_SLIDE_PS_RESYNC_MS);
  }, [runUpdate]);

  const runScrollSnapBurst = useCallback(
    (durationMs = CONTENT_CHANGE_SNAP_BURST_MS) => {
      if (!stickToBottomOnContentChange) return;
      if (scrollSnapBurstRafRef.current) {
        cancelAnimationFrame(scrollSnapBurstRafRef.current);
      }
      const freezeStick = stickToBottomRef.current;
      const start = performance.now();
      const tick = () => {
        if (freezeStick) stickToBottomRef.current = true;
        snapScrollPosition();
        if (performance.now() - start < durationMs) {
          scrollSnapBurstRafRef.current = requestAnimationFrame(tick);
        } else {
          scrollSnapBurstRafRef.current = 0;
        }
      };
      snapScrollPosition();
      scrollSnapBurstRafRef.current = requestAnimationFrame(tick);
    },
    [snapScrollPosition, stickToBottomOnContentChange]
  );

  const handlePsSync = useCallback((ps) => {
    psRef.current = ps;
    ps?.update?.();
  }, []);

  /** 手風琴／展開收合按鈕按下前呼叫，記住是否在底部 */
  const captureStickToBottom = useCallback(() => {
    const container = getScrollContainer();
    if (!container) return;
    updateStickToBottomFromScroll(container);
  }, [getScrollContainer, updateStickToBottomFromScroll]);

  const handleScrollY = useCallback(
    (container) => {
      if (!container) return;
      clampPerfectScrollbarOverflow(container, contentRef.current);
      updateStickToBottomFromScroll(container);
    },
    [updateStickToBottomFromScroll]
  );

  useLayoutEffect(() => {
    if (!enabled) return;
    scrollRef.current?.updateScroll?.();
    scheduleUpdate();
    if (stickToBottomOnContentChange) {
      runScrollSnapBurst();
    }
    return () => {
      if (scrollSnapBurstRafRef.current) {
        cancelAnimationFrame(scrollSnapBurstRafRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- syncDeps 由呼叫端傳入
  }, [enabled, scheduleUpdate, stickToBottomOnContentChange, runScrollSnapBurst, ...syncDeps]);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = window.setTimeout(scheduleUpdate, MODAL_SLIDE_PS_RESYNC_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, scheduleUpdate]);

  useEffect(() => {
    const contentEl = contentRef.current;
    if (!enabled || !contentEl || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const ro = new ResizeObserver(() => {
      if (stickToBottomOnContentChange) {
        snapScrollPosition();
      }
      scheduleUpdate();
    });
    ro.observe(contentEl);
    return () => ro.disconnect();
  }, [enabled, scheduleUpdate, snapScrollPosition, stickToBottomOnContentChange]);

  return {
    scrollRef,
    contentRef,
    handlePsSync,
    scheduleUpdate,
    handleScrollY,
    captureStickToBottom,
    runScrollSnapBurst,
  };
}
